import {
  PiUser,
  PiIdentificationCard,
  PiRadio,
  PiPhone,
  PiFlag,
  PiDatabase,
  PiBoat,
  PiRuler,
  PiArrowUp,
  PiCompass,
  PiNavigationArrow,
  PiGauge,
  PiMapPin,
  PiClock,
  PiGlobe,
  PiThermometer,
  PiWind,
  PiDropHalf,
  PiMapTrifold,
  PiHash,
  PiWarning,
  PiCheckCircle,
  PiInfo,
  PiTarget,
  PiAirplaneTilt,
  PiSignIn,
} from 'react-icons/pi';
import { TargetCardTargetType } from './useTargetForm';

// Field display names per target type
export const FIELD_DISPLAY_NAMES: Record<string, string> = {
  // Common fields
  name: 'Name',
  mmsi: 'MMSI',
  country: 'Country',
  latitudeDegrees: 'Latitude',
  longitudeDegrees: 'Longitude',
  lastOrigin: 'Last Origin',
  targetType: 'Target Type',

  // Vessel fields
  imo: 'IMO',
  callSign: 'Call Sign',
  aisType: 'AIS Type',
  sourceType: 'Source Type',
  shipType: 'Ship Type',
  width: 'Width',
  length: 'Length',
  draught: 'Draught',
  heading: 'Heading',
  rateOfTurn: 'Rate of Turn',
  courseOverGround: 'Course Over Ground',
  speedOverGround: 'Speed Over Ground',
  destination: 'Destination',
  eta: 'ETA',
  navigationalStatus: 'Navigational Status',

  // ATON fields
  assignedMode: 'Assigned Mode',
  atonType: 'AtoN Type',
  offPosition: 'Off Position',
  state: 'State',
  status: 'Status',

  // Virtual ATON fields
  virtualAtonType: 'Virtual AtoN Type',

  // MetHyd fields
  stationName: 'Station Name',
  siteId: 'Site ID',
  airRelatedSettings: 'Air Related',
  waterRelatedSettings: 'Water Related',
  location: 'Location',
  message: 'Message',
  reportTimestamp: 'Report Timestamp',
  dataTimeout: 'Data Timeout',
  owner: 'Owner',

  // BaseStation fields
  vdlLoad: 'VDL Load',

  // Radar fields
  id: 'ID',
  lastRadarReport: 'Last Radar Report',

  // DAS fields
  trackId: 'Track ID',
  site: 'Site',
  priority: 'Priority',
  lastReportTimestamp: 'Last Report',
  accuracy: 'Accuracy',
  speed: 'Speed',

  // Drone fields
  uasId: 'UAS ID',
  macAddress: 'MAC Address',
  uaType: 'UA Type',
  altitude: 'Altitude',
  operatorLatitude: 'Operator Latitude',
  operatorLongitude: 'Operator Longitude',
  operationStatus: 'Operation Status',
};

// Get field display name with fallback
export const getFieldDisplayName = (field: string): string => {
  return FIELD_DISPLAY_NAMES[field] || field;
};

// Field icons mapping
export const getFieldIcon = (fieldName: string) => {
  switch (fieldName) {
    // Identification
    case 'name':
    case 'stationName':
      return PiUser;
    case 'imo':
    case 'id':
    case 'trackId':
    case 'uasId':
      return PiIdentificationCard;
    case 'mmsi':
      return PiRadio;
    case 'callSign':
    case 'macAddress':
      return PiPhone;

    // Location
    case 'country':
      return PiFlag;
    case 'latitudeDegrees':
    case 'longitudeDegrees':
    case 'operatorLatitude':
    case 'operatorLongitude':
      return PiGlobe;
    case 'destination':
    case 'location':
      return PiMapPin;
    case 'site':
      return PiMapTrifold;

    // Type/Category
    case 'aisType':
    case 'sourceType':
    case 'atonType':
    case 'virtualAtonType':
    case 'uaType':
      return PiDatabase;
    case 'shipType':
    case 'targetType':
      return PiBoat;

    // Dimensions
    case 'width':
    case 'length':
      return PiRuler;
    case 'draught':
      return PiArrowUp;
    case 'altitude':
      return PiAirplaneTilt;

    // Navigation
    case 'heading':
    case 'rateOfTurn':
      return PiCompass;
    case 'courseOverGround':
      return PiNavigationArrow;
    case 'speedOverGround':
    case 'speed':
      return PiGauge;
    case 'navigationalStatus':
    case 'operationStatus':
      return PiBoat;

    // Time
    case 'eta':
    case 'lastRadarReport':
    case 'reportTimestamp':
    case 'lastReportTimestamp':
    case 'dataTimeout':
      return PiClock;

    // MetHyd specific
    case 'airRelatedSettings':
      return PiWind;
    case 'waterRelatedSettings':
      return PiDropHalf;
    case 'message':
      return PiInfo;

    // Status
    case 'assignedMode':
      return PiCheckCircle;
    case 'offPosition':
      return PiWarning;
    case 'state':
    case 'status':
      return PiCheckCircle;
    case 'vdlLoad':
      return PiGauge;

    // DAS specific
    case 'priority':
      return PiHash;
    case 'accuracy':
      return PiTarget;
    case 'lastOrigin':
      return PiSignIn;

    // Owner
    case 'owner':
      return PiUser;
    case 'siteId':
      return PiHash;

    default:
      return PiDatabase;
  }
};

// Get the title for each target type tab
export const getTargetTypeTitle = (targetType: TargetCardTargetType): string => {
  switch (targetType) {
    case 'vessel':
      return 'Vessel';
    case 'aton':
      return 'AtoN';
    case 'virtualAton':
      return 'Virtual AtoN';
    case 'methyd':
      return 'Met/Hyd';
    case 'baseStation':
      return 'Base Station';
    case 'radar':
      return 'Radar';
    case 'das':
      return 'DAS';
    case 'drone':
      return 'Drone';
    default:
      return 'Unknown';
  }
};
